import { useEffect, useRef, useState } from "react";
import useStateRef from "./useStateRef";
import { ProgressMetadata } from "@factorearth/component-library";

/**
 *
 * @param initialValues the starting keys and values for the form
 * @param onSubmitInput callback to be called on submit
 * type T is a generic type that allows this to be loosey goosey without just using Record<string, any>
 * type K is an extension of the type T, this is very specific to this situation since a lot of different things
 * can use this hook, but we want to be able to use the same function for different types of objects.
 * Different inputs are going to return a different thing there, and certain functionality expects certain
 * things to be a lot of different things, so hence generic type for now.
 * @returns the hooks
 */
function useCustomForm<T>(
	initialValues: T,
	onSubmitInput?: (item: T) => Promise<void>,
	setProgress?: (newProgress: Partial<ProgressMetadata>) => void,
	setImageCount?: React.Dispatch<React.SetStateAction<number>>,
	imageCount?: number,
) {
	const [values, setValues] = useStateRef(initialValues || ({} as T));
	const [changed, setChanged] = useState(false);
	const [loading, setLoading] = useState(false);
	const previousInitialValues = useRef<T>();

	useEffect(() => {
		// This is here because the values are becoming stale after rearrange
		// which is causing the form itself to have old data from whatever was
		// in that position before the rearrange.

		// Only update if the values have actually changed (deep comparison)
		if (JSON.stringify(previousInitialValues.current) !== JSON.stringify(initialValues)) {
			setValues(initialValues);
			previousInitialValues.current = initialValues;
		}
	}, [initialValues]);

	function handleChange<K extends keyof T>(e: { target: { name: K; value: T[K]; type?: string; files?: FileList | null } }, cb?: (obj: T) => void) {
		let { name, value, type, files } = e.target;

		// Batch Upload Images to Photolog is the only place this is used
		if (type === "file" && files) {
			value = files as T[K];
			setImageCount?.(files.length);
		}

		const obj = { ...values, [name]: value };
		setValues((prev) => ({ ...prev, [name]: value }));

		if (!changed) {
			setChanged(true);
		}
		cb?.(obj);
	}

	/**
	 * Callback to update state when a cloud document change sub is fired
	 */
	function handleUpdate(data: T) {
		setValues(data);
	}

	/**
	 * Submit callback which validates the inputs from validate and handles any errors then
	 * calls the provided callback
	 * @param newValueData event object style for dropdowns that need to update on change.
	 * @param useNewValue is a boolean to tell this callback that you want to use the data from the object passed in. false means it will use whatever is in state for values.
	 */
	const handleSubmit = async <K extends keyof T>(
		newValueData?: { target: { name: K; value: T[K]; type?: string; files?: FileList | null } },
		useNewValue?: boolean,
	) => {
		setLoading(true);
		setProgress?.({
			value: 25,
			variant: "info",
			message: `${imageCount} Images uploading to the photo log`,
			title: "Uploading....",
			canClose: false,
			show: true,
		});
		let newValues = { ...values };
		if (newValueData && useNewValue) {
			const key = newValueData.target.name;
			const newValue = newValueData.target.value;
			const propToUpdate = { [key]: newValue };
			newValues = { ...newValues, ...propToUpdate };
			setProgress?.({
				value: 25,
				variant: "info",
				message: `${imageCount} Images uploading to the photo log`,
				title: "Uploading....",
				canClose: false,
				show: true,
			});
		}

		setProgress?.({
			value: 50,
			variant: "info",
			message: `${imageCount} Images uploading to the photo log`,
			title: "Uploading....",
			canClose: false,
			show: true,
		});
		await onSubmitInput?.(newValues);
		setProgress?.({
			value: 75,
			variant: "info",
			message: `${imageCount} Images uploading to the photo log`,
			title: "Uploading....",
			canClose: false,
			show: true,
		});
		setLoading(false);
		setProgress?.({
			value: 100,
			variant: "success",
			message: `${imageCount} Images uploaded successfully`,
			title: "Upload Complete",
			canClose: true,
			show: true,
			timeout: 5000,
		});
		if (imageCount) setValues(initialValues);
		setImageCount?.(0);
		setTimeout(() => {
			setChanged(false);
		}, 2000);
		// return for async timing
		return true;
	};

	const handleUpdateMultiplePropsAtOnce = async (data: Partial<T>) => {
		let newValues: T = { ...values, ...data };
		setLoading(true);
		setValues((prev) => ({ ...prev, ...newValues }));

		if (onSubmitInput) await onSubmitInput(newValues);
		setLoading(false);
		setTimeout(() => {
			setChanged(false);
		}, 2000);
	};

	const handleImageChangeAndUpload = async <K extends keyof T>(e: { target: { name: K; value: T[K]; type?: string; files?: FileList | null } }) => {
		let { name, value, type, files } = e.target;
		setLoading(true);
		// Batch Upload Images to Photolog is the only place this is used
		if (type === "file" && files) {
			value = files as T[K];
		}

		const obj = { ...values, [name]: value };
		setValues((prev) => ({ ...prev, [name]: value }));

		if (!changed) {
			setChanged(true);
		}

		await onSubmitInput?.(obj);
		setLoading(false);
		setTimeout(() => {
			setChanged(false);
		}, 2000);
		// return for async timing
		return true;
	};
	return {
		values,
		changed,
		handleChange,
		handleUpdate,
		handleSubmit,
		handleImageChangeAndUpload,
		loading,
		handleUpdateMultiplePropsAtOnce,
	};
}

export default useCustomForm;
